import { Alert } from 'components/commons/alert'
import ErrorMessage from 'components/commons/error-message'
import FormModal from 'components/commons/form-modal'
import Input from 'components/commons/input'
import Select from 'components/commons/select'
import {
    OPTIONS_NINE_BOX_LABEL,
    OPTIONS_PERFORMANCE_CATEGORY,
    OPTIONS_POTENTIAL_CATEGORY,
    OPTIONS_THREE_SIXTY_CATEGORY,
} from 'containers/hr-metrics/development-summary/DevelopmentSummaryFilterContainer'
import { useValidation } from 'hooks/useValidation'
import { useEffect, useState } from 'react'
import { API_DEVELOPMENT_SUMMARY } from 'services/apiDevelopmentSummary'
import { useFetch } from 'services/useFetch'
import { constructOptionsSelect, uploadFileToS3 } from 'utils/utils'

const VALIDATION = {
    kpi: {
        maximumValue: {
            value: 100,
            message: 'KPI tidak valid (Maks. 100)',
        },
    },
    attendance: {
        maximumValue: {
            value: 100,
            message: 'Attendance tidak valid (Maks. 100)',
        },
    },
    seminar: {
        maximumValue: {
            value: 100,
            message: 'Seminar tidak valid (Maks. 100)',
        },
    },
    user_review: {
        maximumValue: {
            value: 100,
            message: 'User Review tidak valid (Maks. 100)',
        },
    },
    psychology_test: {
        maximumValue: {
            value: 100,
            message: 'Psychology Test tidak valid (Maks. 100)',
        },
    },
}

export const FormDevelopmentSummaryContainer = ({ data, show, onHide, onSuccess }) => {
    const [form, setForm] = useState({})
    const [errorMessage, setErrorMessage] = useState({})
    const [options, setOptions] = useState({
        threeSixtyCategory: [],
        performanceCategory: [],
        potentialCategory: [],
        nineBoxLabel: [],
    })

    const { fetching: getUrlDocument, isLoading: isLoadingGetUrlDocument } = useFetch(
        API_DEVELOPMENT_SUMMARY.URL_DOCUMENT,
    )
    const { fetching: updateDevelopmentSummary, isLoading: isLoadingUpdateDevelopmentSummary } = useFetch({
        ...API_DEVELOPMENT_SUMMARY.UPDATE,
        initPathVariable: form?.id,
    })

    const { errorForm, validateForm } = useValidation({ form: form, schema: VALIDATION })

    useEffect(() => {
        const threeSixtyCategory = constructOptionsSelect(OPTIONS_THREE_SIXTY_CATEGORY, 'name', 'id')
        const performanceCategory = constructOptionsSelect(OPTIONS_PERFORMANCE_CATEGORY, 'name', 'id')
        const potentialCategory = constructOptionsSelect(OPTIONS_POTENTIAL_CATEGORY, 'name', 'id')
        const nineBoxLabel = constructOptionsSelect(OPTIONS_NINE_BOX_LABEL, 'name', 'id')
        setForm({
            ...data,
            three_sixty_category: threeSixtyCategory.find((item) => item.value === data?.three_sixty_category),
            performance_category: performanceCategory.find((item) => item.value === data?.performance_category),
            potential_category: potentialCategory.find((item) => item.value === data?.potential_category),
            nine_box_label: nineBoxLabel.find((item) => item.value === data?.nine_box_label),
        })
        setOptions({
            threeSixtyCategory,
            performanceCategory,
            potentialCategory,
            nineBoxLabel,
        })
    }, [data])

    const onChangeForm = (e) => {
        const { name, value } = e
        setForm({ ...form, [name]: value })
    }

    const onHideModal = () => {
        setForm({})
        setErrorMessage({})
        onHide()
    }

    const onUpload = async (e, name) => {
        const file = e.target.files[0]
        if (!file) return

        const allowedExtensions = /(\.pdf)$/i
        if (!allowedExtensions.exec(file?.name)) {
            e.target.value = null
            return Alert({
                icon: 'error',
                title: 'Failed',
                message: 'Format file tidak sesuai',
            })
        }

        if (file?.size > 21000000) {
            e.target.value = null
            return Alert({
                icon: 'error',
                title: 'Failed',
                message: 'File terlalu besar (Maksimal 20 MB)',
            })
        }

        const reader = new FileReader()
        reader.onload = () => {
            e.target.value = null
            setForm({ ...form, [name]: file })
        }
        reader.readAsDataURL(file)
    }

    const uploadAttachments = async (documents) => {
        if (!documents) return null

        if (documents?.url) return documents

        const { data: s3Urls } = await getUrlDocument({ files: [documents?.name] })
        const uploadedNewFiles = await Promise.all(
            s3Urls.map(async (s3Item) => {
                await uploadFileToS3(s3Item, documents)
                return {
                    name: documents?.name,
                    url: `${s3Item.url}${s3Item.fields.key}`,
                }
            }),
        )
        return uploadedNewFiles?.[0]
    }

    const onSubmitForm = async () => {
        try {
            await validateForm()
            let hasError = false
            const attachmentKeys = ['individual_performance_file', 'appraisal_file']
            const uploadedAttachments = {}
            await Promise.all(
                attachmentKeys.map(async (key) => {
                    const documents = form[key]
                    if (documents?.name) {
                        try {
                            uploadedAttachments[key] = await uploadAttachments(documents)
                        } catch (error) {
                            if (error?.code === '400-AD005') {
                                setErrorMessage((prev) => ({ ...prev, [key]: error?.data?.errors?.[0]?.message }))
                                hasError = true
                            }
                        }
                    }
                }),
            )
            setForm({ ...form, ...uploadedAttachments })

            if (hasError) return

            const { employee, id, ...payload } = form
            const payloadUpdate = {
                ...payload,
                three_sixty_category: payload?.three_sixty_category?.value,
                nine_box_label: payload?.nine_box_label?.value,
                performance_category: payload?.performance_category?.value,
                potential_category: payload?.potential_category?.value,
                individual_performance_file: uploadedAttachments?.individual_performance_file?.url,
                appraisal_file: uploadedAttachments?.appraisal_file?.url,
            }

            await updateDevelopmentSummary({ ...payloadUpdate })
            onHideModal()
            onSuccess()
        } catch (error) {
            console.log('error', error)
        }
    }

    return (
        <FormModal
            width='max-w-[1024px]'
            show={show}
            title={`Update Data`}
            onHide={onHideModal}
            onSubmitForm={onSubmitForm}
            isLoading={isLoadingGetUrlDocument || isLoadingUpdateDevelopmentSummary}
        >
            <div className={'flex flex-row px-6 py-4 gap-8'}>
                <div className={'flex-1 space-y-6'}>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Nama Karyawan'}</div>
                        <Input className='flex-1' name='employee_name' value={form?.employee?.full_name} disabled />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'NIP'}</div>
                        <Input className='flex-1' name='employee_nip' value={form?.employee?.nip} disabled />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Job Title'}</div>
                        <Input
                            className='flex-1'
                            name='job_title'
                            placeholder='Isi job title'
                            value={form?.job_title}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Department'}</div>
                        <Input
                            className='flex-1'
                            name='department'
                            placeholder='Isi department'
                            value={form?.department}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Division'}</div>
                        <Input
                            className='flex-1'
                            name='division'
                            placeholder='Isi division'
                            value={form?.division}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md mt-2'>{'KPI'}</div>
                        <div>
                            <Input
                                className='flex-1'
                                name='kpi'
                                type='float'
                                maxLength={5}
                                placeholder='Isi kpi'
                                value={form?.kpi}
                                onChange={onChangeForm}
                            />
                            <ErrorMessage className={'mt-2'} message={errorForm?.kpi} />
                        </div>
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md mt-2'>{'Attendance'}</div>
                        <div>
                            <Input
                                className='flex-1'
                                name='attendance'
                                type='float'
                                maxLength={5}
                                placeholder='Isi attendance'
                                value={form?.attendance}
                                onChange={onChangeForm}
                            />
                            <ErrorMessage className={'mt-2'} message={errorForm?.attendance} />
                        </div>
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md mt-2'>{'Seminar'}</div>
                        <div>
                            <Input
                                className='flex-1'
                                name='seminar'
                                type='float'
                                maxLength={5}
                                placeholder='Isi seminar'
                                value={form?.seminar}
                                onChange={onChangeForm}
                            />
                            <ErrorMessage className={'mt-2'} message={errorForm?.seminar} />
                        </div>
                    </div>
                </div>

                <div className={'flex-1 space-y-6'}>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'360 Category'}</div>
                        <Select
                            name='three_sixty_category'
                            className='flex-1'
                            placeholder='Pilih 360 Category'
                            options={options?.threeSixtyCategory}
                            value={form?.three_sixty_category}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md mt-2'>{'User Review'}</div>
                        <div>
                            <Input
                                className='flex-1'
                                name='user_review'
                                type='float'
                                maxLength={5}
                                placeholder='Isi user review'
                                value={form?.user_review}
                                onChange={onChangeForm}
                            />
                            <ErrorMessage className={'mt-2'} message={errorForm?.user_review} />
                        </div>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Performance Category'}</div>
                        <Select
                            name='performance_category'
                            className='flex-1'
                            placeholder='Pilih Performance Category'
                            options={options?.performanceCategory}
                            value={form?.performance_category}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md mt-2'>{'Psychology Test'}</div>
                        <div>
                            <Input
                                className='flex-1'
                                name='psychology_test'
                                type='float'
                                maxLength={5}
                                placeholder='Isi psychology test'
                                value={form?.psychology_test}
                                onChange={onChangeForm}
                            />
                            <ErrorMessage className={'mt-2'} message={errorForm?.psychology_test} />
                        </div>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'Potential Category'}</div>
                        <Select
                            name='potential_category'
                            className='flex-1'
                            placeholder='Pilih Potential Category'
                            options={options?.potentialCategory}
                            value={form?.potential_category}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 text-md'>{'9 Box Label'}</div>
                        <Select
                            name='nine_box_label'
                            className='flex-1'
                            placeholder='Pilih 9 Box Label'
                            options={options?.nineBoxLabel}
                            value={form?.nine_box_label}
                            onChange={onChangeForm}
                        />
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 mt-2'>{'Individual Performance File'}</div>
                        <div className='flex-1 flex flex-col gap-2'>
                            <div className='flex-1 flex items-center gap-2'>
                                {form?.individual_performance_file?.name ? (
                                    <span className='font-medium flex-1'>
                                        {form?.individual_performance_file?.name}
                                    </span>
                                ) : (
                                    form?.individual_performance_file && (
                                        <span className='font-medium flex-1 break-all truncate w-40 [direction:rtl] text-left'>
                                            {form?.individual_performance_file}
                                        </span>
                                    )
                                )}
                                <input
                                    className={'hidden'}
                                    accept='application/pdf'
                                    id='individual_performance_file'
                                    type={'file'}
                                    onChange={(e) => onUpload(e, 'individual_performance_file')}
                                />
                                <label
                                    className='avo__button outline cursor-pointer'
                                    type='button'
                                    htmlFor={'individual_performance_file'}
                                >
                                    <div className='btn-content'>
                                        {`Upload ${form?.individual_performance_file ? 'Ulang' : 'File'}`}
                                    </div>
                                </label>
                            </div>
                            <ErrorMessage className='mt-2' message={errorMessage?.individual_performance_file} />
                        </div>
                    </div>
                    <div className='flex gap-3'>
                        <div className='font-medium w-1/3 text-neutral-70 mt-2'>{'Appraisal File'}</div>
                        <div className='flex-1 flex flex-col gap-2'>
                            <div className='flex-1 flex items-center gap-2'>
                                {form?.appraisal_file?.name ? (
                                    <span className='font-medium flex-1'>{form?.appraisal_file?.name}</span>
                                ) : (
                                    form?.appraisal_file && (
                                        <span className='font-medium flex-1 break-all truncate w-40 [direction:rtl] text-left'>
                                            {form?.appraisal_file}
                                        </span>
                                    )
                                )}
                                <input
                                    className={'hidden'}
                                    accept='application/pdf'
                                    id='appraisal_file'
                                    type={'file'}
                                    onChange={(e) => onUpload(e, 'appraisal_file')}
                                />
                                <label
                                    className='avo__button outline cursor-pointer'
                                    type='button'
                                    htmlFor={'appraisal_file'}
                                >
                                    <div className='btn-content'>
                                        {`Upload ${form?.appraisal_file ? 'Ulang' : 'File'}`}
                                    </div>
                                </label>
                            </div>
                            <ErrorMessage className='mt-2' message={errorMessage?.appraisal_file} />
                        </div>
                    </div>
                </div>
            </div>
        </FormModal>
    )
}
